/**
 * Didactic Depth Assessment
 * Evaluates explanation and distractor educational quality
 *
 * Step 5 additions:
 * - cognitiveLevel field in DepthScore
 * - solutionSteps[] presence check for tier >= 7
 * - Correct-answer value reference check for MC questions
 * - Minimum distractor explanation length check (< 20 chars)
 */

import { Question } from '../content'
import {
  isGenericText,
  isDidactic,
  isSubstantiveExplanation,
  hasModuleSpecificDepth,
  GENERIC_EXPLANATION_PATTERNS,
  GENERIC_DISTRACTOR_PATTERNS,
  GET_MISCONCEPTIONS,
  SWW_RULES,
  MET_FORMULA,
  TGL_CALC,
  TGL_ERRORS,
  SPR_RULES,
  TAA_CITE,
  STU_STRATEGY,
  SWW_CHECK_STRATEGY,
  NON_MC_TYPES,
} from './utils'
import type { SeverityLevel } from './utils'
import {
  getTierBand,
  getMinExplanationLength,
  getMinDistractorLength,
} from './pipeline/policyEngine'
import { detectCognitiveLevel, type CognitiveLevel } from './cognitiveLevel'

export interface DepthIssue {
  type: 'explanation' | 'distractor'
  severity: SeverityLevel
  message: string
  optionIndex?: number
}

export interface DepthScore {
  score: number
  maxScore: number
  issues: DepthIssue[]
  /** Step 5: cognitive level of the question (recall/comprehension/application/analysis) */
  cognitiveLevel?: CognitiveLevel
}

/**
 * Assess explanation didactic depth — STRENGTHENED
 */
export function assessExplanationDepth(question: Question): DepthScore {
  const issues: DepthIssue[] = []
  let score = 30

  const text = question.feedback?.explanationText?.trim() ?? ''
  const tier = question.tier
  const module = question.module
  const band = getTierBand(tier)
  const cognitiveLevel = detectCognitiveLevel(question)
  const minLength = getMinExplanationLength(tier)

  // Missing explanation — always critical
  if (!text) {
    issues.push({
      type: 'explanation',
      severity: 'critical',
      message: 'Missing explanation text (MASTER_2_2)',
    })
    return { score: 0, maxScore: 30, issues, cognitiveLevel }
  }

  // Generic/boilerplate check — always critical
  if (isGenericText(text, GENERIC_EXPLANATION_PATTERNS)) {
    issues.push({
      type: 'explanation',
      severity: 'critical',
      message:
        'Boilerplate explanation — does not explain concept or calculation (MASTER_2_2)',
    })
    score -= 25 // was 20 — more punishing
  }

  // Tier-aware minimum length (STRENGTHENED)
  if (text.length < minLength) {
    const sev = tier >= 9 ? 'critical' : tier >= 5 ? 'coach' : 'info'
    issues.push({
      type: 'explanation',
      severity: sev as SeverityLevel,
      message: `Explanation too short (${text.length} chars, tier ${tier} requires ≥${minLength})`,
    })
    score -= sev === 'critical' ? 15 : sev === 'coach' ? 8 : 3
  }

  // Module-specific depth requirements — UPGRADED severity for high tiers
  if (!hasModuleSpecificDepth(text, module)) {
    const moduleMessages: Record<string, string> = {
      Getalbegrip: 'GET explanation must show calculation (e.g., "3 × 4 = 12")',
      Basisrekenen:
        'REK explanation must show calculation steps or strategy name',
      SpellingWW:
        'SWW explanation must cite spelling rule (stam, kofschip, dt-regel)',
      SpellingRegel:
        'SPR explanation must cite spelling rule (open/gesloten lettergreep, trema)',
      Metriek:
        'MET explanation must reference formula or conversion (×10, L×B, π × r²)',
      Tijd: 'TIJD explanation must show actual calculation with time units',
      Geld: 'GELD explanation must show actual calculation with euro/cent',
      Taalbegrip: 'TAA explanation must cite text or grammar rule',
      Studievaardig: 'STU explanation must describe strategy',
      Topo: 'TOPO explanation must reference location or geographic fact',
    }
    if (moduleMessages[module]) {
      // UPGRADED: critical for tiers >= 9, coach for tiers >= 5
      const sev = tier >= 9 ? 'critical' : 'coach'
      issues.push({
        type: 'explanation',
        severity: sev as SeverityLevel,
        message: moduleMessages[module],
      })
      score -= sev === 'critical' ? 15 : 10
    }
  }

  // Tier-specific: SWW intermediate should include check strategy
  if (band === 'intermediate' && module === 'SpellingWW') {
    if (!SWW_CHECK_STRATEGY.test(text)) {
      issues.push({
        type: 'explanation',
        severity: 'coach', // upgraded from info
        message:
          'SWW tier 5-8 should include check strategy (ik-vorm, hij-vorm)',
      })
      score -= 5
    }
  }

  // Tier-specific: advanced/expert should show step-by-step reasoning
  if ((band === 'advanced' || band === 'expert') && !isDidactic(text)) {
    issues.push({
      type: 'explanation',
      severity: 'critical', // UPGRADED from warning for advanced/expert
      message: `Tier ${tier} explanation must show step-by-step reasoning`,
    })
    score -= 12
  }

  // Step 5: solutionSteps[] check for tier >= 7
  if (tier >= 7) {
    const solutionSteps = question.feedback?.solutionSteps
    if (!solutionSteps || solutionSteps.length === 0) {
      issues.push({
        type: 'explanation',
        severity: tier >= 11 ? 'coach' : 'info', // upgraded for high tiers
        message: `Tier ${tier} should have solutionSteps[] for step-by-step display`,
      })
      score -= tier >= 11 ? 4 : 2
    }
  }

  // Step 5: correct-answer value reference check for MC questions
  if (
    question.questionType === 'multiple_choice' &&
    Array.isArray(question.options) &&
    question.options.length > 0
  ) {
    const correctOptionText =
      question.options[question.correctAnswer as number] ?? ''
    const plainCorrect = correctOptionText
      .replace(/\$[^$]+\$/g, '')
      .trim()
      .toLowerCase()
    if (plainCorrect.length > 2 && !text.toLowerCase().includes(plainCorrect)) {
      issues.push({
        type: 'explanation',
        severity: 'info',
        message:
          'Explanation should reference the correct answer value explicitly',
      })
      score -= 3
    }
  }

  // NEW: Substantive explanation check for tiers >= 7
  if (tier >= 7 && !isSubstantiveExplanation(text, module)) {
    // Only add if we haven't already flagged as generic or missing depth
    const alreadyFlagged = issues.some(
      (i) =>
        i.message.includes('Boilerplate') ||
        i.message.includes('must show') ||
        i.message.includes('must cite') ||
        i.message.includes('must reference') ||
        i.message.includes('must describe'),
    )
    if (!alreadyFlagged) {
      issues.push({
        type: 'explanation',
        severity: 'coach',
        message: `Tier ${tier} explanation lacks substantive educational content — needs concrete examples, rules, or calculations`,
      })
      score -= 5
    }
  }

  return { score: Math.max(0, score), maxScore: 30, issues, cognitiveLevel }
}

/**
 * Assess distractor didactic depth — STRENGTHENED
 */
export function assessDistractorDepth(question: Question): DepthScore {
  const issues: DepthIssue[] = []
  let score = 25

  // BUG-5 fix: extended non-MC guard (uses shared NON_MC_TYPES from utils.ts)
  if (NON_MC_TYPES.includes(question.questionType)) {
    return { score: 25, maxScore: 25, issues: [] }
  }

  const options = question.options ?? []
  const feedbackMap = question.feedback?.distractorExplanations ?? {}
  const module = question.module
  const tier = question.tier
  const minDistractorLength = getMinDistractorLength(tier)

  // Check each option has feedback
  options.forEach((_, idx) => {
    const exp = feedbackMap[String(idx)]
    if (!exp || exp.trim().length === 0) {
      // UPGRADED: critical for tiers >= 7 (was coach for all)
      const sev = tier >= 7 ? 'critical' : 'coach'
      issues.push({
        type: 'distractor',
        severity: sev as SeverityLevel,
        message: `Missing feedback for option ${idx}`,
        optionIndex: idx,
      })
      score -= sev === 'critical' ? 8 : 5
    }
  })

  // Check for generic distractor explanations
  const wrongOptions = Object.entries(feedbackMap).filter(
    ([idx]) => Number(idx) !== question.correctAnswer,
  )

  wrongOptions.forEach(([idx, exp]) => {
    if (typeof exp !== 'string') return

    // Generic check
    if (isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)) {
      issues.push({
        type: 'distractor',
        severity: 'critical',
        message: `Generic distractor explanation for option ${idx} — must reference specific misconception (MASTER_2_3)`,
        optionIndex: Number(idx),
      })
      score -= 10 // was 8
    }

    // Tier-aware minimum length check (STRENGTHENED)
    if (
      exp.trim().length < minDistractorLength &&
      !isGenericText(exp, GENERIC_DISTRACTOR_PATTERNS)
    ) {
      const sev = tier >= 9 ? 'coach' : 'info'
      issues.push({
        type: 'distractor',
        severity: sev as SeverityLevel,
        message: `Distractor explanation for option ${idx} too short (${exp.trim().length} chars, tier ${tier} requires ≥${minDistractorLength})`,
        optionIndex: Number(idx),
      })
      score -= sev === 'coach' ? 4 : 2
    }
  })

  // Step 5: correct-answer explanation minimum length check
  const correctExp = feedbackMap[String(question.correctAnswer)]
  if (
    typeof correctExp === 'string' &&
    correctExp.trim().length > 0 &&
    correctExp.trim().length < 20
  ) {
    issues.push({
      type: 'distractor',
      severity: 'coach',
      message: `Correct-answer explanation too short (${correctExp.trim().length} chars) — should affirm WHY it is correct`,
      optionIndex: question.correctAnswer as number,
    })
    score -= 4
  }

  // Module-specific misconception checks — UPGRADED severity
  const hasMisconceptionRef = wrongOptions.some(([, exp]) => {
    if (typeof exp !== 'string') return false
    switch (module) {
      case 'Getalbegrip':
        return GET_MISCONCEPTIONS.test(exp) || isDidactic(exp)
      case 'SpellingWW':
        return SWW_RULES.test(exp)
      case 'SpellingRegel':
        return SPR_RULES.test(exp)
      case 'Metriek':
        return MET_FORMULA.test(exp)
      case 'Tijd':
      case 'Geld':
        return TGL_ERRORS.test(exp) || isDidactic(exp)
      case 'Basisrekenen':
        return /\d+\s*[×x\*÷\/\+\-]\s*\d+/.test(exp) || isDidactic(exp)
      case 'Taalbegrip':
        return TAA_CITE.test(exp)
      case 'Studievaardig':
        return STU_STRATEGY.test(exp)
      default:
        return isDidactic(exp)
    }
  })

  if (wrongOptions.length > 0 && !hasMisconceptionRef) {
    const moduleMessages: Record<string, string> = {
      Getalbegrip:
        'GET distractors should reference misconception (plaatswaarde, komma, afronden)',
      Basisrekenen:
        'REK distractors should reference calculation or strategy error',
      SpellingWW: 'SWW distractors should reference spelling rule error',
      SpellingRegel: 'SPR distractors should reference spelling rule error',
      Metriek: 'MET distractors should reference formula or unit error',
      Tijd: 'TIJD distractors should reference time unit/notation error',
      Geld: 'GELD distractors should reference euro/cent notation error',
      Taalbegrip: 'TAA distractors should reference grammar or text error',
      Studievaardig: 'STU distractors should reference strategy error',
      Topo: 'TOPO distractors should reference geographic misconception',
    }
    if (moduleMessages[module]) {
      // UPGRADED: critical for tiers >= 9
      const sev = tier >= 9 ? 'critical' : 'coach'
      issues.push({
        type: 'distractor',
        severity: sev as SeverityLevel,
        message: moduleMessages[module],
      })
      score -= sev === 'critical' ? 10 : 7
    }
  }

  return { score: Math.max(0, score), maxScore: 25, issues }
}
